import { useEffect, useRef, useState } from "react";
import { parseProgramProgress, type ProgramProgress } from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { ProgressByProgram } from "./usePrograms";

export type UseEditedProgramProgressOptions = {
  apiFetch: ProgramApiFetch;
  /** The host's cache of the markers the Plan surface last read (mobile
   *  AsyncStorage, web localStorage). Absent: the server only. */
  readCached?: () => Promise<ProgressByProgram | null>;
};

// Where the caller is in the program the editor opens (#336 slice 5): its
// executed rounds are locked. The cached marker first, so it holds offline,
// then the server's, for any program, followed or not. No program (a new
// one): null, nothing locked. A failed fetch keeps the cached marker. Lifted
// from the mobile screen for slice 6: the hosts inject only the reads.
export function useEditedProgramProgress(
  programId: string | undefined,
  opts: UseEditedProgramProgressOptions,
): ProgramProgress | null {
  const [progress, setProgress] = useState<ProgramProgress | null>(null);
  // Hosts may pass fresh literals per render: the effect reads the latest.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });
  useEffect(() => {
    if (!programId) return;
    let cancelled = false;
    void (async () => {
      const { apiFetch, readCached } = optsRef.current;
      const cached = (await readCached?.())?.[programId] ?? null;
      if (!cancelled && cached) setProgress(cached);
      try {
        const raw = await apiFetch<unknown>(`/api/me/programs/${encodeURIComponent(programId)}/progress`);
        if (!cancelled) setProgress(parseProgramProgress(raw));
      } catch {
        // Offline or refused: the cached marker stands.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [programId]);
  return progress;
}
